import { useState } from 'react';
import '../../style/react-select.css';
import styles from './ModalContent.module.css';
import {
  EnteredInputs,
  ModalInput,
  StandardModalContent,
} from './ModalStackContext';
import { MODAL_STARTING_Z_INDEX } from './ModalStackProvider';
import StandardModalBody from './StandardModalBody';
import StandardModalButtons from './StandardModalButtons';
import StandardModalHeader from './StandardModalHeader';

interface ModalContentProps {
  /** Unique ID assigned to the modal to distinguish from other modals in the stack */
  id: string;
  /**
   * Content to be displayed on the modal
   *
   * To display a standard modal, populate the relevant `StandardModalContent` properties.
   * To display an entirely custom modal with custom styling, pass in the custom modal's JSX content.
   * */
  content: StandardModalContent | JSX.Element;
  /** class name to use for the content box */
  className?: string;
  /** index of the modal within the modal stack */
  index: number;
  /** Function to remove this modal from the modal stack */
  popThisModal?: () => void;
  /** Whether this modal should close when the background is clicked */
  closeOnBackgroundClick?: boolean;
}

/** UI component to display a single modal */
export default function ModalContent(props: ModalContentProps): JSX.Element {
  const {
    id,
    content,
    className,
    index,
    popThisModal,
    closeOnBackgroundClick = true,
  } = props;
  const z = MODAL_STARTING_Z_INDEX + index;

  // Create an object with all of the inputs' ids and default to empty string values
  // This `inputValues` object's values will get populated as input values are entered
  const [inputValues, setInputValues] = useState<EnteredInputs>(
    'inputs' in content && content.inputs
      ? content.inputs.reduce(
          (inputs: EnteredInputs, input: ModalInput) => ({
            ...inputs,
            [input.id]: null,
          }),
          {},
        )
      : {},
  );

  function handleOk() {
    if (popThisModal) {
      popThisModal();
    }
    if ('body' in content && content.onOk) {
      content.onOk(inputValues);
    }
  }

  function handleCancel() {
    if (popThisModal) {
      popThisModal();
    }
    if ('body' in content && content.onCancel) {
      content.onCancel();
    }
  }

  return (
    <div
      key={id}
      style={{ zIndex: z }}
      onClick={closeOnBackgroundClick ? () => handleCancel() : undefined}
      className={styles.background}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={className || styles['content-box']}
      >
        {
          'body' in content ? (
            <>
              <StandardModalHeader {...content} onCancel={handleCancel} />
              <StandardModalBody
                {...content}
                inputValues={inputValues}
                onChangeInputs={setInputValues}
              />
              <StandardModalButtons
                {...content}
                onCancel={handleCancel}
                onOk={handleOk}
                inputValues={inputValues}
              />
            </>
          ) : (
            content
          ) // Custom modal content
        }
      </div>
    </div>
  );
}
